import { useId, type ReactNode, type SelectHTMLAttributes } from "react";

export interface SelectFieldProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label: ReactNode;
  options: { value: string; label: string; disabled?: boolean }[];
  /** Подпись видна только читалке: сортировка над списком. */
  hideLabel?: boolean;
  hint?: ReactNode;
  error?: ReactNode;
}

/**
 * Выбор из короткого списка: нативный select. На телефоне это системный
 * барабан, он удобнее любого своего списка в перчатке. Подсказка и ошибка
 * как у TextField: формы документов показывают их одинаково.
 */
export function SelectField({ label, options, hideLabel, hint, error, id, className, ...rest }: SelectFieldProps) {
  const autoId = useId();
  const selectId = id ?? autoId;
  const hintId = hint ? `${selectId}-hint` : undefined;
  const errorId = error ? `${selectId}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(" ") || undefined;
  return (
    <div className={className ? `w-field ${className}` : "w-field"}>
      <label htmlFor={selectId} className={hideLabel ? "w-visually-hidden" : "w-field-label"}>
        {label}
      </label>
      <select id={selectId} className="w-input w-select" aria-invalid={error ? true : undefined} aria-describedby={describedBy} {...rest}>
        {options.map((o) => (
          <option key={o.value} value={o.value} disabled={o.disabled}>
            {o.label}
          </option>
        ))}
      </select>
      {hint ? (
        <span id={hintId} className="w-field-hint">
          {hint}
        </span>
      ) : null}
      {error ? (
        <span id={errorId} className="w-field-error" role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
}
